<script setup lang="ts">
    import { useI18n } from 'vue-i18n'
    import { useForm } from '@volverjs/form-vue'
    import * as z from 'zod'

    const { t: $t } = useI18n({ useScope: 'global' })

    defineProps<{
        submitted?: boolean
        loading?: boolean
        error?: string
    }>()

    const emit = defineEmits<{
        submit: [comment: string]
        close: []
    }>()

    const FeedbackFormSchema = z.object({
        comment: z.string().min(1).default(''),
    })

    const { VvForm, VvFormField, formData } = useForm(FeedbackFormSchema, {
        lazyLoad: true,
    })

    const onSubmit = (data: { comment: string }) => {
        emit('submit', data.comment)
    }
</script>

<template>
    <div
        class="border border-surface-3 rounded-xl w-full overflow-hidden bg-surface">
        <div
            class="px-sm py-6 bg-surface-1 min-h-40 text-12 border-b border-surface-3 text-word-3 flex items-center gap-8 min-h-40">
            <VvIcon name="ri:feedback-line" class="text-16" />
            <strong class="font-bold">{{ $t('action.feedback') }}</strong>
            <VvButton
                icon="ri:close-line"
                modifiers="action-quiet-small"
                :title="$t('action.close')"
                class="ml-auto"
                @click.stop="$emit('close')" />
        </div>
        <Transition mode="out-in">
            <div v-if="!submitted">
                <VvForm v-model="formData" @submit="onSubmit">
                    <div class="p-sm">
                        <VvAlert
                            v-if="error"
                            modifiers="danger"
                            class="mb-sm"
                            :content="error" />
                        <VvFormField
                            name="comment"
                            type="textarea"
                            rows="3"
                            :label="$t('label.comment')"
                            :placeholder="$t('placeholder.comment')"
                            modifiers="compact no-label"
                            class="mb-0" />
                    </div>
                    <div
                        class="flex justify-end border-t border-surface-3 px-sm py-10">
                        <VvButtonGroup>
                            <VvButton
                                type="button"
                                modifiers="action-quiet"
                                icon="ri:close-line"
                                icon-position="right"
                                :label="$t('action.close')"
                                @click="$emit('close')" />
                            <VvButton
                                type="submit"
                                modifiers="action-primary"
                                icon="ri:send-plane-2-line"
                                icon-position="right"
                                :loading
                                :disabled="loading"
                                :label="$t('action.submit')" />
                        </VvButtonGroup>
                    </div>
                </VvForm>
            </div>
            <div v-else class="p-sm">
                <!-- One confirmation row across every tool card: a neutral
                     check, the title in weight, the body quiet. A completed
                     submission is a confirmation, not an alarm (DESIGN.md). -->
                <div class="flex items-center gap-sm">
                    <VvIcon name="ri:check-line" class="text-16 text-word-3" />
                    <div>
                        <strong class="font-medium block text-word-1">
                            {{ $t('message.feedbackSubmittedTitle') }}
                        </strong>
                        <p class="text-word-3">
                            {{ $t('message.feedbackSubmittedMessage') }}
                        </p>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>
